import { executionOf, ExecutionErrors } from "./CicdExecution";
import { useEffect, useId, useState } from "react";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { useTranslation } from "react-i18next";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplayIcon from "@mui/icons-material/Replay";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { transportPackageError } from "../utils/transportPackageErrors";
import "./CicdRunHistory.css";

const text = value => typeof value === "string" ? value.trim() : "";
const timestamp = value => value ? new Date(value).getTime() : NaN;

// Only the selected Space's exact environment mapping identifies an instance.
// Older runs and ambiguous mappings retain the recorded environment instead of guessing.
export function instanceNameForEnvironment(environment, stages = []) {
  const env = text(environment);
  if (!env) return "?";
  const matches = stages.filter(stage => text(stage.targetEnv).toLocaleLowerCase() === env.toLocaleLowerCase());
  return matches.length === 1 && text(matches[0].instanceName) ? text(matches[0].instanceName) : env;
}

export function timeAgo(iso, t, now = Date.now()) {
  const start = timestamp(iso);
  if (!Number.isFinite(start)) return "";
  const seconds = Math.max(0, Math.floor((now - start) / 1000));
  if (seconds < 60) return t("gemeinsam:zeit.geradeEben");
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t("gemeinsam:zeit.vorMinuten", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("gemeinsam:zeit.vorStunden", { count: hours });
  return t("zeit.vorTagen", { count: Math.floor(hours / 24) });
}

export function duration(start, end, t, now = Date.now()) {
  const from = timestamp(start), to = end ? timestamp(end) : now;
  if (!Number.isFinite(from) || !Number.isFinite(to)) return "";
  const seconds = Math.max(0, Math.floor((to - from) / 1000));
  if (seconds < 60) return t("dauer.sekunden", { sek: seconds });
  return t("dauer.minuten", { min: Math.floor(seconds / 60), sek: seconds % 60 });
}

export function runStatus(run, t) {
  const execution = executionOf(run);
  if (execution) {
    const cls = { running: "run", queued: "wait", pending: "wait", completed: "ok", partial: "wait", failed: "bad" }[execution.status] || "grey";
    return { cls, label: t(`execution.status.${execution.status}`, { defaultValue: t("execution.status.unknown") }) };
  }
  if (run.status === "completed" && run.conclusion === "success" && run.meta?.outcome
      && ["failed", "pending", "unknown"].includes(run.meta.outcome.edge))
    return { cls: "wait", label: t(run.meta.outcome.edge === "failed" ? "zustand.cloudSuccessEdgeFailed" : "zustand.cloudSuccessEdgePending") };
  if (run.timedOut) return { cls: "grey", label: t("zustand.zeitueberschreitung") };
  const pending = {
    queued: "warteschlange", in_progress: "laeuft", waiting: "wartet",
    pending: "ausstehend", requested: "angefordert",
  };
  if (pending[run.status]) return {
    cls: run.status === "in_progress" ? "run" : "wait", label: t(`zustand.${pending[run.status]}`),
  };
  if (run.status !== "completed") return { cls: "grey", label: text(run.status) || t("zustand.unbekannt") };
  const conclusions = {
    success: ["ok", "erfolgreich"], failure: ["bad", "fehlgeschlagen"],
    cancelled: ["grey", "abgebrochen"], skipped: ["grey", "uebersprungen"],
    neutral: ["grey", "neutral"], timed_out: ["bad", "zeitueberschreitung"],
    action_required: ["wait", "aktionNoetig"], stale: ["grey", "veraltet"],
    startup_failure: ["bad", "startFehler"],
  };
  const known = conclusions[run.conclusion];
  return known ? { cls: known[0], label: t(`zustand.${known[1]}`) }
    : { cls: "grey", label: text(run.conclusion) || t("zustand.beendet") };
}

function Status({ run, t, workflow = false }) {
  const status = runStatus(run, t);
  const Icon = { ok: CheckCircleOutlineRoundedIcon, bad: ErrorOutlineRoundedIcon,
    run: PlayCircleOutlineRoundedIcon, wait: HourglassTopRoundedIcon }[status.cls]
    || (run.timedOut ? ScheduleRoundedIcon : run.conclusion === "cancelled" ? BlockRoundedIcon
      : run.status === "completed" ? InfoOutlinedIcon : HelpOutlineRoundedIcon);
  return <span className={`crh-status crh-status--${status.cls}`}>
    <Icon aria-hidden="true" />{workflow && !executionOf(run) && status.cls === "ok" ? t("zustand.workflowSuccessful") : status.label}
  </span>;
}

function RunDetails({ run, effective, data, artifacts, t, locale, rerunId, onRerun, rerunNotice, rerunNoticeAction, disconnected, preserveDetailContent }) {
  const meta = run.meta || {};
  const fallback = t("details.nichtGespeichert");
  const facts = [
    ["runId", run.id], ["runNumber", run.runNumber],
    ["actor", text(run.actor) || text(data?.run?.actor) || fallback],
    ["event", text(run.event) || text(data?.run?.event) || fallback],
    ["originalTitle", text(run.title) || text(data?.run?.title) || fallback],
    ...(text(meta.packageId) ? [["packageId", meta.packageId]] : []),
    ...(text(meta.ref) ? [[meta.kind === "release" ? "releaseTag" : "branch", meta.ref]] : []),
    ...(text(meta.kind) ? [["kind", meta.kind === "release" ? t("details.release") : meta.kind === "transport" ? t("details.transport") : meta.kind]] : []),
    ...(Number.isFinite(timestamp(meta.at)) ? [["at", new Date(meta.at).toLocaleString(locale)]] : []),
  ];
  const jobs = data?.run?.jobs;
  const retainJobs = preserveDetailContent && Array.isArray(jobs);
  const rerunnable = data?.run && !data.loading && !data.error
    && (effective.conclusion === "failure" || effective.conclusion === "cancelled");
  const canRerun = rerunnable && onRerun;
  // Ohne Neustart (etwa mit Stufenfreigabe) zeigt die Seite stattdessen, wo wiederholt wird.
  const notice = rerunnable && !onRerun && rerunNotice;
  const reason = text(data?.error?.message) || text(data?.error);
  const htmlUrl = run.htmlUrl || data?.run?.htmlUrl || data?.run?.html_url;
  return <>
    <ExecutionErrors execution={executionOf(data?.run) || executionOf(effective)} details={data?.run?.errorDetails} targetInstanceId={meta.targetInstanceId} />
    {data?.run?.errorsUnavailable && <div className="crh-notice crh-notice--error" role="alert">{t("execution.detailsUnavailable")}</div>}
    <div className="crh-detail-grid">
    <div className="crh-artifacts">
      <h3><Inventory2OutlinedIcon aria-hidden="true" />{t("details.artifacts")}
        {artifacts.length > 0 && <span className="crh-section-count">{artifacts.length}</span>}</h3>
      {artifacts.length ? <ul>{artifacts.map((artifact, index) => <li key={`${artifact}-${index}`}>
        <AccountTreeOutlinedIcon aria-hidden="true" /><span>{artifact}</span>
      </li>)}</ul>
        : <p>{t("details.keineArtefakte")}</p>}
    </div>
    <div className="crh-jobs" aria-busy={Boolean(data?.loading)}>
      <h3><AccountTreeOutlinedIcon aria-hidden="true" />{t("details.jobs")}</h3>
      {retainJobs && data?.loading && <LinearProgress aria-label={t("details.laden")}/>}
      {retainJobs && data?.error && <Alert severity="warning" variant="outlined">{t("details.fehler")}{reason && ` ${reason}`}</Alert>}
      {data?.loading && !retainJobs ? <p role="status">{t("details.laden")}</p>
        : data?.error && !retainJobs ? <div className="crh-notice crh-notice--error" role="alert">
          <strong>{t("details.fehler")}</strong>{reason && <p>{reason}</p>}
        </div>
          : data?.run?.jobsError ? <div className="crh-notice crh-notice--error" role="alert">
            <strong>{t("details.fehler")}</strong><p>{t("details.jobsHttpFehler", { status: data.run.jobsError.status })}</p>
          </div>
          : Array.isArray(jobs) && jobs.length ? jobs.map((job, index) =>
            <section className="crh-job" key={job.id ?? index}>
              <div className="crh-job-head"><h4>{job.name || t("details.jobFallback", { n: index + 1 })}</h4><Status run={job} t={t} /></div>
              {job.steps?.length ? <ol className="crh-steps">{job.steps.map((step, stepIndex) =>
                <li key={step.number ?? stepIndex}>
                  <span>{step.name || t("details.stepFallback", { n: stepIndex + 1 })}</span><Status run={step} t={t} />
                </li>)}</ol> : <p>{t("details.keineSchritte")}</p>}
            </section>)
            : <p>{t(data?.run ? "details.keineJobs" : "details.nichtGeladen")}</p>}
    </div>
    </div>
    <div className="crh-record">
      <h3><InfoOutlinedIcon aria-hidden="true" />{t("details.laufdaten")}</h3>
      <dl className="crh-facts">
        {facts.filter(([, value]) => value !== undefined && value !== null).map(([key, value]) =>
          <div key={key} className={key === "originalTitle" ? "crh-fact-wide" : undefined}>
            <dt>{t(`details.${key}`)}</dt><dd>{value}</dd>
          </div>)}
      </dl>
    </div>
    {notice && <div className="crh-notice" role="note" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
      <span>{notice}</span>{rerunNoticeAction}</div>}
    {(canRerun || htmlUrl) && <div className="crh-actions">
      {canRerun && <Button className="crh-button" variant="outlined" startIcon={<ReplayIcon />}
        disabled={rerunId === run.id || disconnected} onClick={() => onRerun(run)}>
        {t(rerunId === run.id ? "aktion.neustartLaeuft" : "aktion.neustart")}
      </Button>}
      {htmlUrl && <Button className="crh-button" component="a" href={htmlUrl}
        target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewIcon />}>
        {t("aktion.github")}
      </Button>}
    </div>}
  </>;
}

// --- Legeaufträge (DEV183): Einlegeaufträge stehen als Einträge in der Laufhistorie, im selben Kartendesign. ---------
const JOB_ACTIVE = new Set(["queued", "running", "reconciling"]);
const JOB_LOOK = { queued: "wait", running: "run", reconciling: "wait", done: "ok", failed: "bad", cancelled: "grey" };
const JOB_ICON = { wait: HourglassTopRoundedIcon, run: PlayCircleOutlineRoundedIcon, ok: CheckCircleOutlineRoundedIcon, bad: ErrorOutlineRoundedIcon, grey: BlockRoundedIcon };
const jobState = job => (JOB_LOOK[job?.state] ? job.state : "reconciling");
const jobStart = job => job.startedAt || job.createdAt || job.updatedAt;
const jobTargets = job => (Array.isArray(job.packages) ? job.packages : []).filter(p => p?.packageId && p.packageId !== job.packageId);
const jobArtifacts = job => (Array.isArray(job.result?.pakete) ? job.result.pakete : []).flatMap(p => Array.isArray(p?.artefakte) ? p.artefakte : [])
  .map(a => (typeof a === "string" ? a : a?.id || a?.targetId || "")).filter(Boolean);
// DEV183: Hinweise aus dem Ergebnis des Legeauftrags (notices, warnings, hints mit PACKAGE_* bzw. REFERENCE_*), je Code einmal.
const jobNotices = (job, language) => [...new Map(["notices", "warnings", "hints"].flatMap(key => (Array.isArray(job.result?.[key]) ? job.result[key] : []))
  .filter(w => w && typeof w === "object" && /^(PACKAGE|REFERENCE)_/.test(String(w.code || "")))
  .map(w => [String(w.code).startsWith("REFERENCE_") ? `${w.code}:${w.referenceId || ""}` : `${w.code}:${w.packageId || ""}`, w])).values()]
  .map(w => ({ code: w.code, text: text(w.message) || transportPackageError(w, language) || w.code,
    severity: w.code === "REFERENCE_SKIPPED_UNLOADABLE" ? "warning" : "info" }));
const jobCommit = job => {
  const sha = job.result?.commit?.sha || job.result?.commit?.commit?.sha || job.result?.sourceSha;
  return typeof sha === "string" && /^[a-f0-9]{7,40}$/i.test(sha) ? sha : null;
};
/** Suche „Paket oder Artefakt“ auch über Legeaufträge. */
export function jobMatches(job, query) {
  const needle = text(query).toLocaleLowerCase();
  if (!needle) return true;
  return [job.packageName, job.packageId, ...(job.packages || []).flatMap(p => [p?.packageId, p?.packageName]), ...jobArtifacts(job)]
    .some(value => text(value).toLocaleLowerCase().includes(needle));
}

function JobCommit({ sha, t }) {
  const [copied, setCopied] = useState(false);
  const canCopy = typeof navigator !== "undefined" && typeof navigator.clipboard?.writeText === "function";
  useEffect(() => { if (!copied) return undefined; const timer = setTimeout(() => setCopied(false), 2000); return () => clearTimeout(timer); }, [copied]);
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
    <Tooltip title={sha}><span style={{ fontFamily: "var(--iio-font-mono)" }}>{sha.slice(0, 7)}</span></Tooltip>
    {canCopy && <Tooltip title={t(copied ? "legeauftrag.kopiert" : "legeauftrag.commitKopieren")}>
      <IconButton size="small" aria-label={t("legeauftrag.commitKopieren")} sx={{ color: "var(--iio-text-3)" }}
        onClick={async () => { try { await navigator.clipboard.writeText(sha); setCopied(true); } catch { setCopied(false); } }}>
        <ContentCopyRoundedIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>}
  </span>;
}

function JobEntry({ job, id, t, locale, source, spaceId, expanded, onToggle, onCancel, cancelling }) {
  const state = jobState(job);
  const cls = JOB_LOOK[state];
  const Icon = JOB_ICON[cls];
  const start = jobStart(job);
  const active = JOB_ACTIVE.has(state);
  const absoluteTime = Number.isFinite(timestamp(start)) ? new Date(start).toLocaleString(locale) : "";
  const date = absoluteTime ? new Date(start).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "";
  const clock = absoluteTime ? new Date(start).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) : "";
  const elapsed = job.startedAt && (active || job.finishedAt) ? duration(job.startedAt, active ? null : job.finishedAt, t) : "";
  const targets = jobTargets(job);
  const artifacts = jobArtifacts(job);
  const sha = jobCommit(job);
  const summaryId = `${id}-job-${job.id}-summary`, panelId = `${id}-job-${job.id}-panel`;
  return <Accordion id={`snapshot-job-${job.id}`} data-job-id={job.id} className={`crh-run crh-run--${cls}`} disableGutters elevation={0}
    expanded={expanded} onChange={() => onToggle(job.id)} TransitionProps={{ unmountOnExit: true, timeout: 0 }}>
    <AccordionSummary component="button" type="button" className="crh-summary" id={summaryId} aria-controls={panelId} expandIcon={<ExpandMoreIcon aria-hidden="true" />}>
      <span className="crh-status-tab"><span className={`crh-status crh-status--${cls}`}><Icon aria-hidden="true" />{t(`legeauftrag.zustand.${state}`)}</span></span>
      <span className="crh-main">
        <span className="crh-package-icon"><Inventory2OutlinedIcon aria-hidden="true" /></span>
        <span className="crh-package-content">
          <span className="crh-title">{job.packageName || job.packageId}</span>
          <span className="crh-preview">{artifacts.length
            ? <>{artifacts.slice(0, 3).join(", ")}{artifacts.length > 3 && <> · {t("lauf.weitereArtefakte", { count: artifacts.length - 3 })}</>}</>
            : t("legeauftrag.titel")}</span>
          {artifacts.length > 0 && <span className="crh-history-note">{t("legeauftrag.titel")}</span>}
        </span>
      </span>
      <span className="crh-route">
        <span className="crh-stage crh-stage--source"><span className="crh-stage-icon"><StorageRoundedIcon aria-hidden="true" /></span>
          <span><span className="crh-stage-label">{t("lauf.quelle")}</span>{" "}<strong>{source || "-"}</strong></span></span>
        <span className="crh-route-chevrons" aria-hidden="true"><ChevronRightRoundedIcon /><ChevronRightRoundedIcon /><ChevronRightRoundedIcon /></span>
        <span className="crh-stage crh-stage--target"><span className="crh-stage-icon"><FlagOutlinedIcon aria-hidden="true" /></span>
          <span><span className="crh-stage-label">{t("lauf.ziel")}</span>{" "}<strong>{targets.length
            ? targets.map(p => p.packageName || p.packageId).join(", ") : t("legeauftrag.ziel")}</strong></span></span>
      </span>
      <span className="crh-time">
        {absoluteTime ? <><time dateTime={new Date(start).toISOString()} title={absoluteTime}>
          <CalendarTodayOutlinedIcon aria-hidden="true" /><span>{date} · {clock}</span>
        </time><span className="crh-age">{timeAgo(start, t)}</span></> : <span>{t("lauf.zeitUnbekannt")}</span>}
        <span className="crh-duration"><ScheduleRoundedIcon aria-hidden="true" />{elapsed ? t("lauf.dauer", { zeit: elapsed }) : t("lauf.dauerUnbekannt")}</span>
      </span>
    </AccordionSummary>
    <AccordionDetails className="crh-details">
      {jobNotices(job, locale.startsWith("en") ? "en" : "de").map((notice, index) => <Alert key={`${notice.code}:${index}`} severity={notice.severity} variant="outlined"
        sx={{ mb: 1, borderRadius: "6px", color: "var(--iio-text-1)", backgroundColor: "transparent", overflowWrap: "anywhere",
          borderColor: notice.severity === "warning" ? "var(--iio-warning)" : "var(--iio-border)",
          "& .MuiAlert-icon": { color: notice.severity === "warning" ? "var(--iio-warning)" : "var(--iio-text-2)" } }}>{notice.text}</Alert>)}
      {job.error?.message && <Alert severity="error" variant="outlined" sx={{ mb: 1.5, borderRadius: "6px", color: "var(--iio-text-1)",
        borderColor: "var(--iio-critical)", backgroundColor: "transparent", overflowWrap: "anywhere", "& .MuiAlert-icon": { color: "var(--iio-critical)" } }}>
        {job.error.message}</Alert>}
      <div className="crh-record">
        <h3><InfoOutlinedIcon aria-hidden="true" />{t("legeauftrag.daten")}</h3>
        <dl className="crh-facts">
          {sha && <div><dt>{t("legeauftrag.commit")}</dt><dd><JobCommit sha={sha} t={t} /></dd></div>}
          {targets.length > 0 && <div className="crh-fact-wide"><dt>{t("legeauftrag.werkspakete")}</dt><dd>{targets.map(p => p.packageName || p.packageId).join(", ")}</dd></div>}
          {Number.isInteger(job.position) && active && <div><dt>{t("legeauftrag.position")}</dt><dd>{job.position}</dd></div>}
          {text(job.userName) && <div><dt>{t("legeauftrag.benutzer")}</dt><dd>{job.userName}</dd></div>}
          {text(job.topicName) && <div><dt>{t("legeauftrag.topic")}</dt><dd>{job.topicName}</dd></div>}
        </dl>
      </div>
      {((state === "queued" && job.canCancel === true && onCancel) || state === "done") && <div className="crh-actions">
        {state === "queued" && job.canCancel === true && onCancel && <Button className="crh-button" variant="outlined"
          disabled={cancelling === job.id} onClick={() => onCancel(job)}>{t("legeauftrag.abbrechen")}</Button>}
        {state === "done" && spaceId && <Button className="crh-button" variant="outlined" component="a"
          href={`/approvals?spaceId=${encodeURIComponent(spaceId)}`}>{t("legeauftrag.freigabe")}</Button>}
      </div>}
    </AccordionDetails>
  </Accordion>;
}

/**
 * Pure presentation: the parent owns queries, paging, expansion and requests.
 * detail is keyed by run ID: { [id]: { loading, error, run: { status, conclusion, jobs } } }.
 * Detail status overrides historyOnly rows, or live rows with an older GitHub updatedAt.
 * Without comparable timestamps, live row status takes precedence over cached detail.
 * onQueryChange receives the input string; onToggleRun and onRerun receive the original run.
 * total and hasMore describe the supplied query result, including stored historical records.
 */
export default function CicdRunHistory({
  runs = [], stages = [], loading = false, error = null, runsUnknown = false, disconnected = false,
  total, hasMore = false, onLoadMore, query = "", onQueryChange, preserveDetailContent = false,
  expandedId = null, detail = {}, onToggleRun, rerunId = null, onRerun, rerunNotice, rerunNoticeAction = null, className = "",
  // DEV183: Legeaufträge (Einlegeaufträge) aus useSnapshotJobs, gemischt mit den Läufen.
  jobs = [], jobSource = "", spaceId = "", selectedJobId = null, onCancelJob, cancellingJobId = null, jobsError = null, onRefreshJobs,
}) {
  const { t, i18n } = useTranslation(["cicdruns", "gemeinsam"]);
  const id = useId();
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "de-DE";
  const searching = Boolean(query.trim());
  const unavailable = Boolean(error || runsUnknown || disconnected);
  const reason = text(error?.message) || text(error);
  const shownJobs = (Array.isArray(jobs) ? jobs : []).filter(job => job?.id && jobMatches(job, query));
  const [openJobId, setOpenJobId] = useState(selectedJobId);
  useEffect(() => { if (selectedJobId) setOpenJobId(selectedJobId); }, [selectedJobId]);
  useEffect(() => {
    if (!selectedJobId || !shownJobs.some(job => job.id === selectedJobId)) return undefined;
    const frame = requestAnimationFrame(() => document.getElementById(`snapshot-job-${selectedJobId}`)?.scrollIntoView?.({ block: "center" }));
    return () => cancelAnimationFrame(frame);
  }, [selectedJobId, shownJobs.length]); // eslint-disable-line react-hooks/exhaustive-deps
  // Aktive Legeaufträge zuerst; die übrigen nach Zeit zwischen die Läufe (deren Reihenfolge bleibt, wie der Server sie liefert).
  const activeJobs = shownJobs.filter(job => JOB_ACTIVE.has(jobState(job))).sort((a, b) => timestamp(jobStart(b)) - timestamp(jobStart(a)));
  const doneJobs = shownJobs.filter(job => !JOB_ACTIVE.has(jobState(job))).sort((a, b) => timestamp(jobStart(b)) - timestamp(jobStart(a)));
  const entries = [...activeJobs.map(job => ({ job }))];
  {
    let next = 0;
    for (const run of runs) {
      const at = timestamp(run.runStartedAt || run.createdAt || run.meta?.at);
      while (next < doneJobs.length && Number.isFinite(at) && timestamp(jobStart(doneJobs[next])) > at) entries.push({ job: doneJobs[next++] });
      entries.push({ run });
    }
    while (next < doneJobs.length) entries.push({ job: doneJobs[next++] });
  }
  const entryCount = runs.length + shownJobs.length;
  const knownTotal = Number.isFinite(total) && total >= runs.length;
  const count = shownJobs.length ? t("kopf.eintraege", { count: entryCount, n: entryCount.toLocaleString(locale) }) : knownTotal
    ? t(searching ? "kopf.treffer" : "kopf.anzahl", { count: total, gezeigt: runs.length.toLocaleString(locale), gesamt: total.toLocaleString(locale) })
    : t("kopf.angezeigt", { count: runs.length, n: runs.length.toLocaleString(locale) });

  return <section className={`iio crh-root ${className}`} aria-labelledby={`${id}-title`}>
    <div className="crh-toolbar">
      <div className="crh-heading"><span className="crh-heading-icon"><HistoryRoundedIcon aria-hidden="true" /></span>
        <div><h2 id={`${id}-title`}>{t("kopf.titel")}</h2><p>{t("kopf.beschreibung")}</p></div>
      </div>
      <TextField className="crh-search" id={`${id}-search`} type="search" size="small" variant="outlined"
        label={t("suche.label")} helperText={t("suche.hinweis")} value={query}
        onChange={event => onQueryChange?.(event.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon aria-hidden="true" /></InputAdornment> }} />
    </div>
    <div className="crh-resultbar">
      <p aria-live="polite" aria-atomic="true">
        {(entryCount > 0 || (!loading && !unavailable)) && count}
      </p>
      {entryCount > 0 && <p className="crh-result-hint"><ExpandMoreIcon aria-hidden="true" />{t("kopf.detailsHinweis")}</p>}
    </div>

    {unavailable && <div className={`crh-notice${error ? " crh-notice--error" : ""}`} role={error ? "alert" : "status"}>
      <strong>{t(error ? "meldung.fehlerTitel" : disconnected ? "meldung.getrenntTitel" : "meldung.unbekanntTitel")}</strong>
      <p>{reason || t(error ? "meldung.fehler" : disconnected ? "meldung.getrennt" : "meldung.unbekannt")}</p>
      {runs.length > 0 && <p>{t("meldung.vorhandene")}</p>}
    </div>}

    {/* DEV183 I2: Lese- oder Abbruchfehler der Legeaufträge; „Aktualisieren“ liest nur die Legeaufträge neu. */}
    {jobsError && <Alert severity="error" variant="outlined" sx={{ mb: 1.5, borderRadius: "6px", color: "var(--iio-text-1)", borderColor: "var(--iio-critical)",
      backgroundColor: "transparent", overflowWrap: "anywhere", "& .MuiAlert-icon": { color: "var(--iio-critical)" } }}
      action={onRefreshJobs ? <Button color="inherit" size="small" onClick={() => onRefreshJobs()}>{t("legeauftrag.aktualisieren")}</Button> : undefined}>
      {t("legeauftrag.fehler")}{text(jobsError?.message || jobsError) ? ` ${text(jobsError?.message || jobsError)}` : ""}</Alert>}

    {loading && <div className="crh-loading" role="status"><p>{t(searching ? "meldung.sucheLaedt" : "meldung.laedt")}</p>
      {!runs.length && <div aria-hidden="true">{[0, 1, 2].map(index =>
        <Skeleton key={index} variant="rounded" height={88} animation={false} />)}</div>}
    </div>}

    {!entryCount && !loading && !unavailable && <div className="crh-empty">
      <span className="crh-empty-icon">{searching ? <SearchIcon aria-hidden="true" /> : <HistoryRoundedIcon aria-hidden="true" />}</span>
      <h3>{t(searching ? "leer.sucheTitel" : "leer.titel")}</h3>
      <p>{t(searching ? "leer.sucheText" : "leer.text", { query: query.trim() })}</p>
      {searching && <p>{t("leer.sucheHinweis")}</p>}
    </div>}

    <div className="crh-list" aria-busy={loading}>
      {entries.map(({ run, job }) => job ? <JobEntry key={`job-${job.id}`} job={job} id={id} t={t} locale={locale} source={jobSource} spaceId={spaceId}
        expanded={openJobId === job.id} onToggle={jobId => setOpenJobId(current => (current === jobId ? null : jobId))}
        onCancel={onCancelJob} cancelling={cancellingJobId} /> : (() => {
        const data = detail?.[run.id];
        const detailRun = data?.run;
        const detailUpdatedAt = detailRun?.updatedAt || detailRun?.updated_at;
        const newerDetail = timestamp(detailUpdatedAt) > timestamp(run.updatedAt);
        const effective = detailRun && (run.historyOnly || newerDetail) ? {
          ...run, ...detailRun, meta: { ...run.meta, ...detailRun.meta },
          timedOut: detailRun.timedOut ?? (detailRun.status === "queued" && run.timedOut),
          createdAt: detailRun.createdAt || detailRun.created_at || run.createdAt,
          runStartedAt: detailRun.runStartedAt || detailRun.run_started_at || run.runStartedAt,
          updatedAt: detailUpdatedAt || run.updatedAt,
        } : run;
        const meta = run.meta || {};
        const artifacts = Array.isArray(meta.artifacts) ? meta.artifacts.filter(artifact => text(artifact)) : [];
        const title = text(meta.packageName) || text(meta.packageId)
          || text(run.title).replace(/\[report:[^\]]*\]/gi, "").replace(/\s+/g, " ").trim()
          || t("lauf.fallback");
        const start = effective.runStartedAt || effective.createdAt || meta.at;
        const age = timeAgo(start, t);
        const absoluteTime = Number.isFinite(timestamp(start)) ? new Date(start).toLocaleString(locale) : "";
        const date = absoluteTime ? new Date(start).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "";
        const clock = absoluteTime ? new Date(start).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) : "";
        const isActive = ["in_progress", "queued", "waiting", "pending", "requested"].includes(effective.status) && !effective.timedOut;
        const elapsed = isActive || (effective.status === "completed" && effective.updatedAt)
          ? duration(effective.runStartedAt || effective.createdAt, isActive ? null : effective.updatedAt, t) : "";
        const summaryId = `${id}-${run.id}-summary`, panelId = `${id}-${run.id}-panel`;
        return <Accordion key={run.id} data-run-id={String(run.id)} className={`crh-run crh-run--${runStatus(effective, t).cls}`} disableGutters elevation={0}
          expanded={String(expandedId) === String(run.id)} onChange={() => onToggleRun?.(run)} TransitionProps={{ unmountOnExit: true, timeout: 0 }}>
          <AccordionSummary component="button" type="button" className="crh-summary" id={summaryId}
            aria-controls={panelId} expandIcon={<ExpandMoreIcon aria-hidden="true" />}>
            <span className="crh-status-tab"><Status run={effective} t={t} workflow /></span>
            <span className="crh-main">
              <span className="crh-package-icon"><Inventory2OutlinedIcon aria-hidden="true" /></span>
              <span className="crh-package-content">
              <span className="crh-title">{title}</span>
              <span className="crh-preview">{artifacts.length
                ? <>{artifacts.slice(0, 3).join(", ")}{artifacts.length > 3 && <> · {t("lauf.weitereArtefakte", { count: artifacts.length - 3 })}</>}</>
                : t("lauf.keineMetadaten")}</span>
              {run.historyOnly && !effective.status && <span className="crh-history-note">{t("lauf.historisch")}</span>}
              {meta.kind === "release" && <span className="crh-release">{t("details.release")}</span>}
              {/* Pakettransport (Welle 6): reiner Text, nur wenn der Server das Kennzeichen liefert. */}
              {meta.transport === "package" && <span className="crh-history-note">{t("lauf.ganzesPaket")}</span>}
              </span>
            </span>
            <span className="crh-route">
              <span className="crh-stage crh-stage--source"><span className="crh-stage-icon"><StorageRoundedIcon aria-hidden="true" /></span>
                <span><span className="crh-stage-label">{t("lauf.quelle")}</span>{" "}<strong title={text(meta.sourceEnv)}>{instanceNameForEnvironment(meta.sourceEnv, stages)}</strong></span></span>
              <span className="crh-route-chevrons" aria-hidden="true">
                <ChevronRightRoundedIcon /><ChevronRightRoundedIcon /><ChevronRightRoundedIcon />
              </span>
              <span className="crh-stage crh-stage--target"><span className="crh-stage-icon"><FlagOutlinedIcon aria-hidden="true" /></span>
                <span><span className="crh-stage-label">{t("lauf.ziel")}</span>{" "}<strong title={text(meta.targetEnv)}>{instanceNameForEnvironment(meta.targetEnv, stages)}</strong></span></span>
            </span>
            <span className="crh-time">
              {absoluteTime ? <><time dateTime={new Date(start).toISOString()} title={absoluteTime}>
                <CalendarTodayOutlinedIcon aria-hidden="true" /><span>{date} · {clock}</span>
              </time><span className="crh-age">{age}</span></> : <span>{t("lauf.zeitUnbekannt")}</span>}
              <span className="crh-duration"><ScheduleRoundedIcon aria-hidden="true" />{elapsed ? t("lauf.dauer", { zeit: elapsed }) : t("lauf.dauerUnbekannt")}</span>
            </span>
          </AccordionSummary>
          <AccordionDetails className="crh-details">
            <RunDetails run={run} effective={effective} data={data} artifacts={artifacts} t={t} locale={locale}
              rerunId={rerunId} onRerun={onRerun} rerunNotice={rerunNotice} rerunNoticeAction={rerunNoticeAction} disconnected={disconnected} preserveDetailContent={preserveDetailContent} />
          </AccordionDetails>
        </Accordion>;
      })())}
    </div>

    {hasMore && <div className="crh-footer"><Button className="crh-button" variant="outlined"
      endIcon={<ExpandMoreIcon aria-hidden="true" />}
      disabled={loading || !onLoadMore} onClick={() => onLoadMore?.()}>{t("aktion.weitere")}</Button></div>}
  </section>;
}
